import React, { createContext, useContext, useRef, useEffect, useCallback, useMemo } from 'react'

import { YStack, TamaguiElement, useTheme } from 'tamagui'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated'

const PILL_SPRING = { mass: 0.15, stiffness: 150, damping: 12 }

type SearchGroupContextType = {
  activeIndex: number
  registerItem: (index: number, el: HTMLElement | null) => void
  registerSelect: (index: number, fn: (() => void) | null) => void
  onHover: (index: number) => void
}

const SearchGroupContext = createContext<SearchGroupContextType>({
  activeIndex: -1,
  registerItem: () => {},
  registerSelect: () => {},
  onHover: () => {},
})

export const useSearchGroup = () => useContext(SearchGroupContext)

export const SearchGroup = ({
  activeIndex,
  onActiveIndexChange,
  selectActiveRef,
  children,
}: {
  activeIndex: number
  onActiveIndexChange: (index: number) => void
  selectActiveRef?: React.MutableRefObject<(() => void) | null>
  children: React.ReactNode
}) => {
  const containerRef = useRef<TamaguiElement>(null)
  const itemsRef = useRef<Map<number, HTMLElement>>(new Map())
  const selectionsRef = useRef<Map<number, () => void>>(new Map())
  const theme = useTheme()

  const pillY = useSharedValue(0)
  const pillHeight = useSharedValue(0)
  const pillOpacity = useSharedValue(0)
  const hasAnimated = useRef(false)

  // Reset animation state when children change (results vs suggestions)
  const childKey = React.Children.count(children)
  useEffect(() => {
    hasAnimated.current = false
    pillOpacity.value = 0
  }, [childKey])

  const registerItem = useCallback((index: number, el: HTMLElement | null) => {
    if (el) itemsRef.current.set(index, el)
    else itemsRef.current.delete(index)
  }, [])

  const registerSelect = useCallback((index: number, fn: (() => void) | null) => {
    if (fn) selectionsRef.current.set(index, fn)
    else selectionsRef.current.delete(index)
  }, [])

  const measure = useCallback(() => {
    const container = containerRef.current as unknown as HTMLElement | null
    if (!container) return

    const item = itemsRef.current.get(activeIndex)
    if (!item) {
      pillOpacity.value = withSpring(0, PILL_SPRING)
      return
    }

    const containerRect = container.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()

    const top = itemRect.top - containerRect.top
    const height = itemRect.height

    if (!hasAnimated.current) {
      pillY.value = top
      pillHeight.value = height
      pillOpacity.value = withSpring(1, PILL_SPRING)
      hasAnimated.current = true
    } else {
      pillY.value = withSpring(top, PILL_SPRING)
      pillHeight.value = withSpring(height, PILL_SPRING)
      pillOpacity.value = withSpring(1, PILL_SPRING)
    }
  }, [activeIndex])

  useEffect(() => {
    measure()
    const raf = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(raf)
  }, [measure])

  /** Called by the parent on Enter key — triggers the active item's select handler */
  const selectActive = useCallback(() => {
    selectionsRef.current.get(activeIndex)?.()
  }, [activeIndex])

  // Expose selectActive to parent via ref
  useEffect(() => {
    if (selectActiveRef) selectActiveRef.current = selectActive
    return () => { if (selectActiveRef) selectActiveRef.current = null }
  }, [selectActive, selectActiveRef])

  const pillStyle = useAnimatedStyle(() => ({
    position: 'absolute' as const,
    top: 0,
    left: 8,
    right: 8,
    height: pillHeight.value,
    transform: [{ translateY: pillY.value }],
    opacity: pillOpacity.value,
    borderRadius: 12,
    backgroundColor: theme.backgroundAccent.val,
  }))

  const contextValue = useMemo(
    () => ({
      activeIndex,
      registerItem,
      registerSelect,
      onHover: onActiveIndexChange,
    }),
    [activeIndex, registerItem, registerSelect, onActiveIndexChange]
  )

  return (
    <SearchGroupContext.Provider value={contextValue}>
      <YStack ref={containerRef} position='relative' padding='$2'>
        <Animated.View
          style={[pillStyle, { pointerEvents: 'none' as const }]}
        />
        {children}
      </YStack>
    </SearchGroupContext.Provider>
  )
}

/** Wrap each interactive item in this to register it with the group pill */
export const SearchGroupItem = ({
  index,
  onSelect,
  children,
}: {
  index: number
  onSelect: () => void
  children: React.ReactNode
}) => {
  const { registerItem, registerSelect, onHover } = useSearchGroup()

  useEffect(() => {
    registerSelect(index, onSelect)
    return () => registerSelect(index, null)
  }, [index, onSelect, registerSelect])

  return (
    <YStack
      ref={(ref) => {
        registerItem(index, ref as unknown as HTMLElement | null)
      }}
      onMouseEnter={() => onHover(index)}
      onPress={onSelect}
      cursor='pointer'
      padding='$3'
      borderRadius='$4'
      position='relative'
      zIndex={1}
    >
      {children}
    </YStack>
  )
}
